/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import DateSelector from '../../components/DateSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as operatorApi from '../../api/CollectionpointOperators';
import * as receptionReports from '../../api/ReceptionReport';
import { GENERATETYPES, ICollectionPointOperator, REPORTSTATUS } from '../../util/DataModelInterface';
import { IReceptionReport, getReceptionReportExcel } from '../../api/ReceptionReport';
import DropdownMultiselect from "react-multiselect-dropdown-bootstrap";
import { formatDateYYYYMM, formatPeriod, getPreviousMonth, getReportTypeString, IMonthValue } from '../../util/DateUtils';
import Modal from '../../components/Modal';

interface IBrowseReceptionReportProps {
  intl: any;
}

const BrowseReceptionReport = (props: IBrowseReceptionReportProps) => {
  const [operator, setOperator] = useState('');
  const [startDate, setStartDate] = useState(getPreviousMonth());
  const [endDate, setEndDate] = useState(getPreviousMonth());
  const [operators, setOperators] = useState([] as ICollectionPointOperator[]);
  const [reportData, setReportData] = useState([] as IReceptionReport[]);
  const [types, setTypes] = useState([] as string[]);
  const [statuses, setStatuses] = useState([] as string[]);
  const [modalOpen, setModalOpen] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [orderColumn, setOrderColumn] = useState('operatorName');
  const [orderDirection, setOrderDirection] = useState(1); // 1 | -1

  useEffect(() => {
    operatorApi.getCollectionpointOperators().then((data) => {
      if (data) {
        setOperators(data);
      }
    });
  }, []);

  const getOperatorList = () => {
    const list: JSX.Element[] = [];
    list.push(<option value="" key="day-0">{props.intl.formatMessage({id:'operator-list.choose'})}</option>);
    operators.forEach((op: ICollectionPointOperator) => list.push(
      <option value={op.id} key={op.id}>{op.name}</option>
    ));
    return list;
  }

  const getTypeSelectList = () => {
    const options: { key: string; label: any; }[] = [];

    GENERATETYPES.forEach((type) =>
      options.push( { key: `${type}`, label: props.intl.formatMessage({id:`report-type.${type}`}) }
    ));

    return(
      <div className="dropdown-div">
        <DropdownMultiselect
          options={options}
          name="reporttypes"
          placeholder= {props.intl.formatMessage({id:'report-createtype.choose'})}
          showSelectToggle="false"
          handleOnChange={(selected: any) => setTypes(selected)}
        />
      </div>);
  }

  const getStatusSelectList = () => {
    const options: { key: string; label: any; }[] = [];

    REPORTSTATUS.forEach((status) =>
      options.push( { key: `${status}`, label: props.intl.formatMessage({id:`report-status.${status}`}) }
    ));

    return(
      <div className="dropdown-div">
        <DropdownMultiselect
        options={options}
        name="reportstatuses"
        placeholder= {props.intl.formatMessage({id:'report-status.choose'})}
        showSelectToggle="false"
        handleOnChange={(selected: any) => setStatuses(selected)}
        />
      </div>);
  }

  const typeFilter = (row: IReceptionReport) => {
    if (types.length === 0 ||
       (types.includes(getReportTypeString(row.periodStart, row.periodEnd, row.autogenReport)))) {
      return true;
    }
    return false;
  }

  const statusFilter = (row: IReceptionReport) => {
    if (statuses.length === 0 
      || ((row.success && !row.empty && statuses.includes('OK'))
      || (!row.success && statuses.includes('FAIL')) 
      || (row.empty && statuses.includes('EMPTY')))) {
      return true;
    }
    return false;
  }

  const comparer = (a: IReceptionReport, b: IReceptionReport) => {
    let value = 0;
    if (orderColumn !== '') {
      switch(orderColumn) {
        case 'latestreport':
          const dateA = a.date ? new Date(a.date) : new Date('1900-01-01');
          const dateB = b.date ? new Date(b.date) : new Date('2099-12-31');
          value = dateA.getTime() - dateB.getTime();
      }
    }
    return orderDirection * value;
  }

  const getOrderIcon = (column: string) => {
    let icon;
    if (column === orderColumn) {
      icon = orderDirection === 1 ?
        <FontAwesomeIcon className="icon" icon="sort-up" color="white" /> : <FontAwesomeIcon className="icon" icon="sort-down" color="white" />
    } else {
      icon = <FontAwesomeIcon className="icon" icon="sort" color="lightgray" />
    }
    return icon;
  }

  const setOrdering = (column: string) => {
    if (orderColumn !== column) {
      setOrderColumn(column);
      setOrderDirection(1);
    } else {
      setOrderDirection(-orderDirection);
    }
  }

  const getDataTable = () => {
    const rows: { }[] = [];
    if (reportData && Array.isArray(reportData)) {
      reportData.filter(typeFilter).filter(statusFilter).sort(comparer).forEach((row: IReceptionReport, index) => {
        const dateTime = `${new Date( row.date ).toLocaleDateString('fi-FI')} ${props.intl.formatMessage({id:'receptionreports.browse.attime'})} ${new Date( row.date ).toLocaleTimeString('fi-FI')}`;
        let infoText = props.intl.formatMessage({id: 'report-status.REPORTSTATUS'}) + ': ';
        infoText +=  row.success ?
                          ( row.empty ? props.intl.formatMessage({id: 'report-status.EMPTY'}) :
                            props.intl.formatMessage({id: 'report-status.OK'})) :
                            props.intl.formatMessage({id: 'report-status.' + row.info});
        if (row.mailStatus)
        {
          const mailInfo = row.mailStatus.info ? `\n[${row.mailStatus.info}]` : '';
          const mailTime = row.mailStatus.time ? `\n${props.intl.formatDate(row.mailStatus.time)} ${props.intl.formatTime(row.mailStatus.time)}` : '';
          infoText += '\n' +
            props.intl.formatMessage({id: 'report-status.MAILSTATUS'}) + ': ' +
            props.intl.formatMessage({id: 'report-status.' + row.mailStatus.status}) +
            mailTime +
            mailInfo;
        }
        rows.push(
          <tr key={index}>
            <td className="listCell">
              <span className="type">
                <FontAwesomeIcon className="reportIcon"
                                  icon={ row.autogenReport ? "print" : "pencil-ruler" }
                                  color={ row.success ? 'var(--serty-color-ok)' : 'var(--serty-color-warning)' } />
                { props.intl.formatMessage({id: 'report-type.' + getReportTypeString(row.periodStart, row.periodEnd, row.autogenReport)}) }
              </span>
            </td>
            <td>
              <span className="reportDate">
                {( row.success ?
                  <a href="#" onClick={() => getReceptionReportExcel(operator, row.reportId) } >
                    { dateTime }
                  </a> :
                  dateTime
                )}
              </span>
            </td>
            <td>
              {formatPeriod(row.periodStart, row.periodEnd, props.intl)}
            </td>
            <td className="listCell">
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip className="info-tooltip" id="tooltip-overlay">
                    <div className="info-tooltip-header">{ infoText }</div>
                  </Tooltip>
                }
              >
                <FontAwesomeIcon className="reportIcon"
                                icon={ row.success ? (row.empty ? "dot-circle" : "check-circle") : "exclamation-circle" }
                                color={ row.success ? (row.empty ? 'var(--serty-color-warning)' : 'var(--serty-color-ok)') : 'var(--serty-color-error)' } />
              </OverlayTrigger>
              { row.success ? (row.empty ?
                props.intl.formatMessage({id: 'report-status.EMPTY'}) :
                props.intl.formatMessage({id: 'report-status.OK'})) :
                props.intl.formatMessage({id: 'report-status.FAIL'}) }
            </td>
          </tr>
        );
      });
      if(rows.length > 0) return rows;
    }
    return (<tr className="waitSpinner"><td colSpan={4}><FormattedMessage id="receptionreports.manage.nodata"/></td></tr>);
  }

  const getModal = () =>
    <Modal
      onClose={() => setModalOpen(false)}
      closeOnClickOutside={false}
      width={400}
      height={250}>
      <div className="ModalView">
        <div className="spinner"/>
        <div className="message"><FormattedMessage id="receptionreports.browse.getting"/></div>
      </div>
    </Modal>

  const fetchData = () => {
    setShowAlert(false)
    setModalOpen(true);
    receptionReports.getReceptionReports(operator, formatDateYYYYMM(startDate), formatDateYYYYMM(endDate))
      .then((data) => {
        setReportData(data);
        setModalOpen(false);
        if (!data || data.length === 0) {
          setShowAlert(true);
        }
      });
  }

  return (
    <div>
      { modalOpen && getModal() }
      <h1>
        <FormattedMessage id="receptionreports.header"/> - <FormattedMessage id="receptionreports.browse.header"/>
      </h1>
      <div className="search-wrap">
        <div className="d-flex flex-row row">
          <div className="col-md-12">
            <div className="form-group">
              <label><FormattedMessage id="receptionreports.browse.operator"/></label>
              <select className="operator-select form-control"
                onChange={(e) => setOperator(e.target.value)}>
                { getOperatorList() }
              </select>
            </div>
          </div>
          <div className="col-md-12 flex-row">
            <div className="form-group">
              <label><FormattedMessage id="receptionreports.browse.monthrange"/></label>
              <div className="d-flex flex-row row">
                <div className="col-md-6 datepicker-input">
                  <DateSelector startPeriod={startDate} endPeriod={endDate}
                                onChange={(start: IMonthValue, end: IMonthValue) => { setStartDate(start); setEndDate(end); } } 
                                dateFormat={'yyyy-MM'} isStartAndEnd={true} />
                </div>
              </div>
            </div>
          </div>
          <div className="form-buttons d-flex flex-row col">
            <button className="btn btn-primary send-button btn-search ml-auto"
              onClick={() => fetchData()}
              disabled={!operator}>
              <FormattedMessage id="receptionreports.browse.getreports"/>
            </button>
          </div>
        </div>
      </div>
      {reportData && Array.isArray(reportData) && reportData.length > 0 &&
      <div className="table-responsive search-results">
        <table className="table table-sm table-striped">
          <thead className="reportDataHeader table table-bordered table-sm">
            <tr>
              <th><FormattedMessage id="report-createtype.choose"/></th>
              <th>
                <span className="sortable" onClick={() => setOrdering('latestreport')}>
                    <FormattedMessage id="receptionreports.browse.datatable.latestreport"/>{getOrderIcon('latestreport')}
                </span>
              </th>
              <th><FormattedMessage id="receptionreports.browse.monthrange"/></th>
              <th><FormattedMessage id="report-status.choose"/></th>
            </tr>
            <tr>
              <th className="listCell"> { getTypeSelectList() }</th>
              <th></th>
              <th></th>
              <th className="listCell"> { getStatusSelectList() }</th>
            </tr>
          </thead>
          <tbody className="reportData table table-bordered">
            { getDataTable() }
          </tbody>
        </table>
      </div>}
      { showAlert &&
      <Alert variant="warning" onClose={() => setShowAlert(false)} dismissible={true}>
        <FormattedMessage id="receptionreports.browse.noreportsfound" />
      </Alert> }
    </div>
  );
}

export default injectIntl(BrowseReceptionReport);
